import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import { useDropzone } from 'react-dropzone';
import UploadIcon from '../assets/data-upload-upload-icon.svg';
import type { FileMetadata, UploadedFileData } from '../../interfaces/UploadedFileDataInterface';
import { USER_ROLES, XQ_SUPPORT_EMAIL } from '../../constants';
import type FileTypesValues from '../../interfaces/FileType';
import UploadPrompt from '../data upload/upload prompts/UploadPrompt';
import { useLocation } from 'react-router-dom';
import useUserDetails from '../hooks/useUserDetails';
import { Accordion, AccordionDetails, AccordionSummary, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Tag from './Tag';
import ArrowRight from '../assets/data-upload-arrow-right.svg';
import ArrowDown from '../assets/data-upload-arrow-down.svg';
import CustomDivider from './CustomDivider';
import UploadCardContext from '../../context/UploadCardContext';
import CustomButton from './CustomButton';
import DownloadIcon from '../assets/download-icon.svg';
import UploadedFilesList from '../data upload/UploadedFilesList';
import CustomAlert from './CustomAlert';

import './UploadCard.css';

export type AcceptFileTypes = {
  [key: string]: string[];
};

interface UploadCardProps {
  title: string;
  helperText: string[];
  uploadedFilesData: UploadedFileData[];
  fileType: FileTypesValues;
  cardId: string;
  maxSize?: number;
  handleSelectedCardId?: Dispatch<SetStateAction<string | null>>;
  sampleFileName?: string | null;
  uploadProgressData?: {
    state: 'pending' | 'progress' | 'completed';
    description: string;
  };
  uploadPrompt?: React.ReactNode;
  onFileSelected?: (file: File) => Promise<void>;
  onFileDelete?: (fileId: string) => void;
  uploadProgressByFileId?: Record<string, number>;
  allowMultiple?: boolean;
  additionalAllowedRoles?: string[];
  acceptFileTypes?: AcceptFileTypes;
  helperFileTypeText?: string;
  showUploadProgress?: boolean;
  showAccordion?: boolean;
  isLocked?: boolean;
}

const MAX_FILE_SIZE = 104857600;

function validateFileSize(file: File, { maxSize }: { maxSize?: number }) {
  if (file.size > Math.max(MAX_FILE_SIZE, maxSize ?? 0)) {
    return {
      code: 'file-too-big',
      message: 'File size too big!',
    };
  }

  return null;
}

function UploadCard({
  title,
  helperText,
  uploadedFilesData,
  fileType,
  cardId,
  maxSize,
  handleSelectedCardId,
  sampleFileName,
  uploadProgressData,
  uploadPrompt,
  onFileSelected,
  onFileDelete,
  uploadProgressByFileId,
  allowMultiple = false,
  additionalAllowedRoles = [],
  acceptFileTypes,
  helperFileTypeText = 'Please upload .csv files only',
  showUploadProgress = true,
  showAccordion = true,
  isLocked = false,
}: UploadCardProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileIdToReplace = useRef<string | null>(null);
  const containerDiv = useRef<HTMLDivElement>(null);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const userRole = userDetails?.role;
  const isFileUploadAllowed =
    !isLocked &&
    [
      USER_ROLES.XQ_ADMIN as string,
      USER_ROLES.DISTRICT_DATA_LEAD as string,
      USER_ROLES.FACILITATOR as string,
      ...additionalAllowedRoles,
    ].includes(userRole as string);

  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const hasAnchorId = location.hash && location.hash.length > 0;
    const shouldScrollToCard = containerDiv.current && location.hash.includes(cardId);

    if (!hasAnchorId || !shouldScrollToCard) return;

    containerDiv.current.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleDrop = (files: File[]) => {
    if (onFileSelected) {
      files.forEach((droppedFile) => onFileSelected(droppedFile));
      return;
    }
    setFile(files[0]);
  };

  const accept = acceptFileTypes ?? {
    'text/csv': ['.csv'],
    // 'application/vnd.ms-excel': ['.xls'],
    // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
  };

  const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
    maxSize: maxSize ?? MAX_FILE_SIZE,
    multiple: allowMultiple,
    onDrop: handleDrop,
    accept,
    validator: (file: File) => validateFileSize(file, { maxSize }),
  });

  const handleClosePrompt = () => {
    setFile(null);
    fileIdToReplace.current = null;
  };

  const handleResubmit = (currentFileId: string) => {
    open();
    fileIdToReplace.current = currentFileId;
  };

  const getFileToReplaceMetadata = () => {
    let fileData = uploadedFilesData.find((fileData) => fileData.file_id === fileIdToReplace.current);
    if (!fileData) return {};

    let previousMetadata = fileData.user_provided_file_metadata;
    let parsedMetadata: FileMetadata = {};

    for (const metadataKey in previousMetadata) {
      let metaData = previousMetadata[metadataKey];
      if (metaData) {
        parsedMetadata[metadataKey] = metaData;
      }
    }

    return parsedMetadata;
  };

  const renderUploadProgress = () => {
    if (!showUploadProgress) return null;

    if (uploadProgressData) {
      return (
        <Tag
          type={uploadProgressData.state}
          text={uploadProgressData.description}
          size="medium"
          className="upload-progress-info"
        />
      );
    } else {
      return <Tag type="error" text="Status unavailable" size="medium" />;
    }
  };

  const renderDropzone = () => {
    if (showUploadProgress && !uploadProgressData) {
      return (
        <CustomAlert alertType="error" header="Upload status error">
          <p className="upload-status-error-message">
            We couldn’t load the file upload status. Please try refreshing the page. If the issue continues,{' '}
            <a href={`mailto:${XQ_SUPPORT_EMAIL}`} className="link-color">
              contact support
            </a>{' '}
            for help.
          </p>
        </CustomAlert>
      );
    } else {
      return (
        <>
          <div className="upload-card-actions">
            {handleSelectedCardId && (
              <CustomButton
                variant="text"
                buttonText="View upload progress"
                buttonAction={() => handleSelectedCardId(cardId)}
                icon={<img src={ArrowRight} alt="open modal / open sidebar" />}
                iconPosition="end"
                sx={{ marginLeft: 'calc(var(--scaling-spacing-16) * -1)' }}
              />
            )}
            {sampleFileName && (
              <a data-testid="example file link" href={`/example files/${sampleFileName}`} download={sampleFileName}>
                <CustomButton
                  variant="text"
                  buttonText="View sample file"
                  icon={<img src={DownloadIcon} alt="download" />}
                  iconPosition="end"
                  buttonAction={() => {}}
                />
              </a>
            )}
          </div>
          <div
            data-testid="upload-card-content"
            className={`upload-card-content ${isSmallScreen ? 'small-screen' : ''}`}
          >
            {isFileUploadAllowed && (
              <div data-testid="drop-zone" className="upload-card-drop-zone">
                <section>
                  <div {...getRootProps()} className={`drop-zone ${isDragAccept ? 'drag-active' : ''}`}>
                    <input data-testid="drop-zone-file-input" {...getInputProps()} />
                    <img src={UploadIcon} alt="upload file icon" className="upload-icon"></img>
                    <p className="drop-zone-text">Drag & drop to upload</p>
                    <span className="drop-zone-text">or</span>
                    <span className="drop-zone-text link-color"> browse files</span>
                    <div className="drop-zone-helper-text-container">
                      <p className="drop-zone-helper-text">{helperFileTypeText}</p>
                      <p className="drop-zone-helper-text">Maximum file size is 100MB</p>
                    </div>
                  </div>
                </section>
                {/* <div className="separator">
                      <span className="separator-text">or</span>
                    </div>
                    <button className="sign-in-button" disabled>Sign in via SIS</button> */}
              </div>
            )}
            {uploadedFilesData && uploadedFilesData.length > 0 ? (
              <UploadedFilesList
                uploadedFilesData={uploadedFilesData}
                fileType={fileType}
                handleResubmit={handleResubmit}
                uploadProgress={uploadProgress}
                progressByFileId={uploadProgressByFileId}
                onFileDelete={onFileDelete}
                isLocked={isLocked}
              />
            ) : null}
          </div>
        </>
      );
    }
  };

  return (
    <>
      <Accordion id={cardId} ref={containerDiv} defaultExpanded>
        <AccordionSummary
          expandIcon={showAccordion ? <img alt="expand accordion icon" src={ArrowDown} /> : null}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={showAccordion ? null : { pointerEvents: 'none' }}
        >
          <div className="upload-card-header">
            <h2>{title}</h2>
            {renderUploadProgress()}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="upload-card-helper-text">
            {helperText.map((text, index) => (
              <p key={index}>{text}</p>
            ))}
          </div>
          {renderDropzone()}
        </AccordionDetails>
      </Accordion>

      {file ? (
        <UploadCardContext.Provider
          value={{
            handleClose: handleClosePrompt,
            fileName: file.name,
            fileType: fileType,
            file: file,
            fileIdToReplace: fileIdToReplace.current,
            handleUploadProgress: setUploadProgress,
            fileMetadata: getFileToReplaceMetadata(),
            triggerFileSelect: open,
          }}
        >
          {uploadPrompt ?? <UploadPrompt />}
        </UploadCardContext.Provider>
      ) : null}
      {showAccordion ? <CustomDivider orientation="horizontal" /> : null}
    </>
  );
}

export default UploadCard;
